/**
 * Department Create Page
 * Page for creating new departments
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
} from '@mui/material';
import { useSnackbar } from 'notistack';

// Components
import { DepartmentForm } from './components';
import { PageShell } from '../../components';

// Services and types
import { departmentService } from '../../services/departmentService';
import type { DepartmentFormData, CreateDepartmentRequest, SafetyCharacteristicType } from '../../types';

const DepartmentCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  // Get pre-selected company from URL params
  const preSelectedCompanyId = searchParams.get('companyId');

  // Handle form submission
  const handleSubmit = async (data: DepartmentFormData) => {
    setLoading(true);
    
    try {
      console.log('🏢 Creating department:', data);
      
      // Convert safety characteristics object to array
      const characteristicsArray = Object.entries(data.safetyCharacteristics)
        .filter(([_, enabled]) => enabled)
        .map(([characteristic]) => characteristic as SafetyCharacteristicType);
      
      const requestData: CreateDepartmentRequest = {
        name: data.name,
        unitId: data.unitId,
        companyId: data.companyId,
        
        location: data.location ?? undefined,
        construction: data.construction ?? undefined,
        ceilingHeight: data.ceilingHeight ?? undefined,
        flooring: data.flooring ?? undefined,
        roofing: data.roofing ?? undefined,
        ventilation: data.ventilation ?? undefined,
        lighting: data.lighting ?? undefined,
        shift: data.shift ?? undefined,
        
        safetyCharacteristics: characteristicsArray,
      };
      
      console.log('📊 Safety characteristics array:', characteristicsArray);
      
      const department = await departmentService.createDepartment(requestData);
      
      console.log('✅ Department created successfully:', department);
      enqueueSnackbar('Setor criado com sucesso!', { variant: 'success' });
      
      // Navigate back to departments list, preserving company filter if it exists
      if (preSelectedCompanyId) {
        navigate(`/departments?companyId=${preSelectedCompanyId}`);
      } else {
        navigate('/departments');
      }
    } catch (error: any) {
      console.error('❌ Error creating department:', error);
      const errorMessage = (error.response?.data?.message || error.message) ?? 'Erro ao criar setor';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (preSelectedCompanyId) {
      navigate(`/departments?companyId=${preSelectedCompanyId}`);
    } else {
      navigate('/departments');
    }
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Setores', path: preSelectedCompanyId ? `/departments?companyId=${preSelectedCompanyId}` : '/departments' },
        { label: 'Novo Setor' },
      ]}
      title="Criar Novo Setor"
      subtitle="Preencha os dados para cadastrar um novo setor no sistema"
    >
      {/* Form Card */}
      <Card>
        <CardContent>
          <DepartmentForm
            mode="create"
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
            preSelectedCompanyId={preSelectedCompanyId ?? undefined}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default DepartmentCreatePage;